import { BookmarkSchema } from "@repo/fika-shared/src/loader/bookmark-schema.js"
import { LogSchema } from "@repo/fika-shared/src/loader/log-schema.js"
import { PostSchema } from "@repo/fika-shared/src/loader/post-schema.js"
import { PublisherSchema } from "@repo/fika-shared/src/loader/publisher-schema.js"
import { RecentPostSchema } from "@repo/fika-shared/src/loader/recent-post-schema.js"
import { RecentPublisherSchema } from "@repo/fika-shared/src/loader/recent-publisher-schema.js"
import { StorySchema } from "@repo/fika-shared/src/loader/story-schema.js"
import { SubscriptionSchema } from "@repo/fika-shared/src/loader/subscription-schema.js"
import { TagSchema } from "@repo/fika-shared/src/loader/tags-schema.js"
import { UserSchema } from "@repo/fika-shared/src/loader/user-schema.js"
import type { ArchivePost } from "@repo/fika-shared/src/mutation/archive-post-schema.js"
import type { BookmarkStory } from "@repo/fika-shared/src/mutation/bookmark-story-schema.js"
import type { CreatePost } from "@repo/fika-shared/src/mutation/create-post-schema.js"
import type { ImportBookmarks } from "@repo/fika-shared/src/mutation/import-bookmarks-schema.js"
import type { ImportSubscriptions } from "@repo/fika-shared/src/mutation/import-subscriptions-schema.js"
import type { PublishPost } from "@repo/fika-shared/src/mutation/publish-post-schema.js"
import type { SetTags } from "@repo/fika-shared/src/mutation/set-tags-schema.js"
import type { SubscribePublisher } from "@repo/fika-shared/src/mutation/subscribe-publisher-schema.js"
import type { UpdateEmailDigest } from "@repo/fika-shared/src/mutation/update-email-digest-schema.js"
import type { UpdateUser } from "@repo/fika-shared/src/mutation/update-user-schema.js"
import type { VersionPost } from "@repo/fika-shared/src/mutation/version-post-schema.js"
import { generate } from "@rocicorp/rails"
import type { WriteTransaction } from "@rocicorp/replicache"
import { ulid } from "ulid"
import { z } from "zod"
import { defaultJsonContent } from "@/lib/defaultJsonContent"

const { set: setRecentPublisher } = generate(
  "recent_publisher",
  safeParse(RecentPublisherSchema),
)
const { set: setRecentPost } = generate(
  "recent_post",
  safeParse(RecentPostSchema),
)
const { init: initPublisher } = generate(
  "publisher",
  safeParse(PublisherSchema),
)
const {
  init: initTag,
  delete: deleteTag,
  list: listTags,
} = generate("tag", safeParse(TagSchema))
const { init: initStory } = generate("story", safeParse(StorySchema))
const {
  delete: deletePost,
  set: setPost,
  update: updatePost,
  get: getPost,
  list: listPosts,
} = generate("post", safeParse(PostSchema))

const { update: _updateUser } = generate("user", safeParse(UserSchema))

const {
  init: initSubscription,
  set: setSubscription,
  delete: deleteSubscription,
} = generate("subscription", safeParse(SubscriptionSchema))

const { delete: deleteBookmark, init: initBookmark } = generate(
  "bookmark",
  safeParse(BookmarkSchema),
)

const { init: initLog } = generate("log", safeParse(LogSchema))

async function updateUser(tx: WriteTransaction, props: UpdateUser) {
  await _updateUser(tx, { ...props, backend: false })
}

async function bookmarkStory(tx: WriteTransaction, props: BookmarkStory) {
  const dc_created_at = props.dc_created_at || new Date().toISOString()

  await Promise.all([
    initStory(tx, {
      id: props.story_id,
      url: props.url,
      dc_title: props.dc_title ?? null,
      dc_description: props.dc_description ?? null,
      logo_url: props.logo_url ?? null,
      image_url: props.image_url ?? null,
      metadated_at: null,
      dc_created_at: dc_created_at,
      feed_url: props.feed_url ?? null,
      summary: null,
      tags: null,
      backend: false,
    }),
    initBookmark(tx, {
      id: props.bookmark_id,
      story_id: props.story_id,
      user_id: props.user_id,
      bookmarked_at: new Date().toISOString(),
      bookmarked_iso: new Date().toISOString().split("T")[0]!,
    }),
  ])
}

async function subscribePublisher(
  tx: WriteTransaction,
  props: SubscribePublisher,
) {
  await Promise.all([
    initPublisher(tx, {
      id: props.publisher_id,
      url: props.url,
      dc_title: null,
      dc_description: null,
      logo_url: null,
      image_url: null,
      dc_created_at: null,
      user_id: null,
      tags: null,
      backend: false,
    }),
    initSubscription(tx, {
      id: props.subscription_id,
      publisher_id: props.publisher_id,
      created_at: new Date().toISOString(),
      silenced: false,
    }),
    initLog(tx, {
      id: ulid(),
      title: "subscribing to publisher",
      status: "Progress",
      created_at: new Date().toISOString(),
    }),
  ])
}

async function publishPost(tx: WriteTransaction, props: PublishPost) {
  const posts = await listPosts(tx)

  for (const post of posts) {
    if (post.root_id !== props.id) continue

    await updatePost(tx, {
      id: post.id,
      published_at: null,
      archived_at: null,
    })
  }

  await updatePost(tx, props)
}

async function archivePost(tx: WriteTransaction, props: ArchivePost) {
  const posts = await listPosts(tx)

  for (const post of posts) {
    if (post.root_id !== props.id) continue

    await updatePost(tx, {
      id: post.id,
      archived_at: null,
      published_at: null,
    })
  }

  if (props.archived_at) {
    await updatePost(tx, props)
  } else {
    await updatePost(tx, {
      id: props.id,
      published_at: new Date().toISOString(),
    })
  }
}

async function versionPost(tx: WriteTransaction, props: VersionPost) {
  const post = await getPost(tx, props.id)

  async function update() {
    await updatePost(tx, {
      id: props.id,
      title: props.title,
      description: props.description,
      json: props.json,
      created_at: new Date().toISOString(),
      published_at: null,
      sent_at: null,
    })
  }

  async function clone() {
    if (!post) return

    await setPost(tx, {
      ...post,
      id: props.version_id,
      latest: false,
    })
  }

  await Promise.all([update(), clone()])
}

async function createPost(tx: WriteTransaction, props: CreatePost) {
  await setPost(tx, {
    ...props,
    root_id: props.id,
    latest: true,
    description: null,
    json: defaultJsonContent,
    created_at: new Date().toISOString(),
    published_at: null,
    archived_at: null,
    backend: false,
    image_header_url: null,
    pinned_at: null,
    canonical_url: null,
    meta_title: null,
    meta_description: null,
    sent_at: null,
  })
}

async function updateEmailDigest(
  tx: WriteTransaction,
  props: UpdateEmailDigest,
) {
  await updateUser(tx, {
    id: props.id,
    email_digest: props.email_digest,
  })
  await initLog(tx, {
    id: ulid(),
    title: "updated email digest",
    status: "Success",
    created_at: new Date().toISOString(),
  })
}

async function setTags(tx: WriteTransaction, props: SetTags) {
  const targetTags = (await listTags(tx)).filter(
    t => t.target_id === props.target_id && t.target_type === props.target_type,
  )

  const existingTagNames = new Set(targetTags.map(t => t.tag))
  const newTagNames = new Set(props.tags.map(t => t.tag))

  const tagsToDelete = existingTagNames.difference(newTagNames)
  for (const tagName of tagsToDelete) {
    const tagToDelete = targetTags.find(t => t.tag === tagName)
    if (tagToDelete) {
      await deleteTag(tx, tagToDelete.id)
    }
  }

  const tagsToCreate = newTagNames.difference(existingTagNames)
  for (const tagName of tagsToCreate) {
    const tagToCreate = props.tags.find(t => t.tag === tagName)
    if (tagToCreate) {
      await initTag(tx, tagToCreate)
    }
  }
}

async function importBookmarks(tx: WriteTransaction, props: ImportBookmarks) {
  const numBookmarks = props.bookmarks.length
  const skippedCopy =
    props.skipped > 0 ? ` (${props.skipped} already existed)` : ""

  await initLog(tx, {
    id: ulid(),
    title:
      numBookmarks === 0
        ? "No bookmarks found in the file."
        : `Started importing ${numBookmarks} bookmarks${skippedCopy}.`,
    status: "Progress",
    created_at: new Date().toISOString(),
  })
}

async function importSubscriptions(
  tx: WriteTransaction,
  props: ImportSubscriptions,
) {
  const numSubscriptions = props.subscriptions.length
  const skippedCopy =
    props.skipped > 0 ? ` (${props.skipped} already existed)` : ""

  await initLog(tx, {
    id: ulid(),
    title:
      numSubscriptions === 0
        ? "No subscriptions found in the file."
        : `Started importing ${numSubscriptions} subscriptions${skippedCopy}.`,
    status: "Progress",
    created_at: new Date().toISOString(),
  })
}

export const mutators = {
  // bookmark
  bookmarkStory,
  deleteBookmark,
  initBookmark,
  importBookmarks,
  // subscription
  deleteSubscription,
  setSubscription,
  subscribePublisher,
  importSubscriptions,
  // recent publisher
  setRecentPublisher,
  // user
  updateUser,
  updateEmailDigest,
  // post
  createPost,
  deletePost,
  archivePost,
  versionPost,
  publishPost,
  updatePost,
  setRecentPost,
  // tag
  setTags,
  // logs
  initLog,
}

export type M = typeof mutators

function safeParse<T>(schema: z.ZodSchema<T>) {
  return (value: unknown): T => {
    const result = schema.safeParse(value)

    if (!result.success) {
      console.error(z.prettifyError(result.error))
      return value as T
    }

    return result.data
  }
}
